import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '@launchpad-ui/components';

import { Card, ErrorMessage, PageHeader, StatusBadge } from '../components/ui';
import { useReviewers } from '../lib/hooks/useReviewers';
import { useLookupZendeskTicket } from '../lib/hooks/useZendeskTicket';

import styles from '../styles/common.module.css';

function assignReviewer(ticketId: number, reviewers: string[]): string {
  if (!reviewers.length) return '—';
  return reviewers[ticketId % reviewers.length];
}

export function TicketLookup() {
  const reviewersQuery = useReviewers();
  const lookup = useLookupZendeskTicket();
  const navigate = useNavigate();
  const [ticketId, setTicketId] = useState('');

  const reviewerNames = (reviewersQuery.data ?? []).filter((r) => r.active).map((r) => r.name);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const id = ticketId.trim();
    if (!id) return;
    lookup.mutate(id);
  };

  const result = lookup.data;
  const reviewer = result ? assignReviewer(result.ticket.id, reviewerNames) : null;

  return (
    <div className={styles.pageNarrow}>
      <PageHeader title="Ticket Lookup" />

      <form onSubmit={submit} style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        <input
          className={styles.input}
          type="text"
          value={ticketId}
          onChange={(e) => setTicketId(e.target.value)}
          placeholder="Enter Zendesk ticket number"
          style={{ flex: 1 }}
        />
        <Button type="submit" isDisabled={lookup.isPending || !ticketId.trim()}>
          {lookup.isPending ? 'Looking up…' : 'Look Up'}
        </Button>
      </form>

      {lookup.error && <ErrorMessage message={(lookup.error as Error).message} />}

      {result && (
        <>
          <Card>
            <div className={styles.spaceBetween}>
              <div>
                <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Ticket #{result.ticket.id}</h2>
                <p style={{ margin: '0.25rem 0 0 0', color: 'var(--lp-color-text-ui-secondary)' }}>
                  {result.ticket.subject}
                </p>
              </div>
              <StatusBadge status={result.ticket.status} />
            </div>
            <dl
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                rowGap: '0.5rem',
                columnGap: '1.5rem',
                marginTop: '1rem',
                fontSize: '0.85rem',
              }}
            >
              <Detail label="Assignee" value={result.assignee?.name ?? '—'} />
              <Detail label="Created" value={new Date(result.ticket.created_at).toLocaleDateString()} />
              <Detail label="Updated" value={new Date(result.ticket.updated_at).toLocaleDateString()} />
              <Detail label="Priority" value={result.ticket.priority ?? '—'} />
            </dl>
          </Card>

          <Card>
            <p className={styles.sectionTitle}>Assigned Reviewer</p>
            <p style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>{reviewer}</p>
            <p style={{ fontSize: '0.75rem', color: 'var(--lp-color-text-ui-secondary)' }}>Assigned by ticket ID</p>
            <div style={{ marginTop: '0.75rem' }}>
              <Button
                onPress={() => {
                  const params = new URLSearchParams({
                    ticket: String(result.ticket.id),
                    topic: result.ticket.subject ?? '',
                  });
                  navigate(`/review?${params.toString()}`);
                }}
              >
                Submit Review
              </Button>
            </div>
          </Card>

          {result.comments && result.comments.length > 0 && (
            <Card>
              <h3 className={styles.sectionTitle}>Comments ({result.comments.length})</h3>
              <div style={{ maxHeight: '20rem', overflowY: 'auto' }}>
                {result.comments.map((c) => (
                  <div
                    key={c.id}
                    style={{
                      padding: '0.5rem 0',
                      borderBottom: '1px solid var(--lp-color-border-ui-secondary)',
                    }}
                  >
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <span style={{ fontWeight: 500, fontSize: '0.85rem' }}>Author #{c.author_id}</span>
                      <span
                        style={{
                          fontSize: '0.7rem',
                          color: 'var(--lp-color-text-ui-secondary)',
                        }}
                      >
                        {new Date(c.created_at).toLocaleString()}
                      </span>
                      {c.public === false && (
                        <span
                          style={{
                            fontSize: '0.65rem',
                            padding: '0.1rem 0.4rem',
                            borderRadius: 4,
                            backgroundColor: 'var(--lp-color-bg-interactive-tertiary)',
                          }}
                        >
                          internal
                        </span>
                      )}
                    </div>
                    <p
                      style={{
                        margin: '0.25rem 0 0 0',
                        fontSize: '0.85rem',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {c.body}
                    </p>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt style={{ color: 'var(--lp-color-text-ui-secondary)' }}>{label}</dt>
      <dd style={{ margin: '0.15rem 0 0 0', fontWeight: 500 }}>{value}</dd>
    </div>
  );
}
